import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Copy, ExternalLink, CheckCircle2, Ban, Send, BellOff, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClients, ClientSelect } from "@/components/ClientSelect";
import { InvoiceDialog } from "@/components/InvoiceDialog";
import { DeleteInvoiceButton } from "@/components/DeleteInvoiceDialog";

const STATUS = { paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", unpaid: "bg-amber-500/15 text-amber-300 border-amber-500/30", void: "bg-slate-700/40 text-slate-400 border-slate-600" };
const payLink = (t) => `${window.location.origin}/pay/${t}`;

export default function Invoices() {
  const [params, setParams] = useSearchParams();
  const clientFilter = params.get("client") || "";
  const { settings, user } = useAuth();
  const clients = useClients();
  const [studies, setStudies] = useState([]);
  const [invoices, setInvoices] = useState(null);
  const [dialog, setDialog] = useState(null);

  const load = () => api.get("/invoices", { params: clientFilter ? { client_id: clientFilter } : {} }).then((r) => setInvoices(r.data));
  useEffect(() => { load(); }, [clientFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/studies").then((r) => setStudies(r.data)); }, []);
  useEffect(() => {
    const study = params.get("study");
    if (study && studies.length) {
      const s = studies.find((x) => x.id === study);
      setDialog({ study_id: study, client_id: s?.client_id || "", tier: s?.input.mode === "address" ? "site" : "standard" });
      params.delete("study");
      setParams(params, { replace: true });
    }
  }, [studies]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (data) => {
    try {
      const r = await api.post("/invoices", data);
      await navigator.clipboard?.writeText(payLink(r.data.token)).catch(() => {});
      toast.success(`${r.data.number} created — payment link copied`);
      setDialog(null);
      load();
    } catch (e) { toast.error(errMsg(e, "Could not create invoice")); }
  };
  const setStatus = async (inv, status) => {
    try { await api.put(`/invoices/${inv.id}/status`, { status }); toast.success(`${inv.number} marked ${status}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const emailInv = async (inv) => {
    try { const r = await api.post(`/invoices/${inv.id}/email`); toast.success(`${inv.number} emailed to ${r.data.sent_to}`); load(); }
    catch (e) { toast.error(errMsg(e, "Could not email invoice")); }
  };
  const togglePause = async (inv) => {
    try { await api.put(`/invoices/${inv.id}/reminders`, { paused: !inv.reminders_paused }); toast.success(inv.reminders_paused ? "Reminders resumed" : "Reminders paused"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const copy = (inv) => navigator.clipboard.writeText(payLink(inv.token)).then(() => toast.success("Payment link copied"));

  const all = invoices || [];
  const sum = (st) => all.filter((i) => i.status === st).reduce((a, i) => a + i.amount, 0);
  const cur = settings?.currency || "USD";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Billing</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Invoices</h1>
        </div>
        <div className="flex flex-wrap sm:flex-nowrap gap-3 w-full sm:w-auto">
          <ClientSelect value={clientFilter} onChange={(v) => setParams(v ? { client: v } : {})} clients={clients} noneLabel="All clients" testId="invoice-filter-client" className="sm:w-56" />
          <Button data-testid="new-invoice-btn" onClick={() => setDialog({ client_id: clientFilter })} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400 shrink-0"><Plus className="h-4 w-4 mr-1" />New invoice</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6 mb-10">
        {[["Outstanding", sum("unpaid"), "kpi-outstanding"], ["Collected", sum("paid"), "kpi-collected"], ["Invoices", all.length, "kpi-invoices"]].map(([l, v, t], i) => (
          <div key={l} className={`rounded-xl border border-slate-800 bg-slate-900/70 p-4 sm:p-6 ${i === 2 ? "col-span-2 sm:col-span-1" : ""}`}>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{l}</p>
            <p data-testid={t} className="num text-2xl sm:text-3xl font-bold">{i < 2 ? money(v, cur) : v}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-slate-800 overflow-x-auto" data-testid="invoices-table">
        <table className="w-full text-sm m-cards">
          <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500">
            <tr>{["Invoice", "Client", "Tier / study", "Amount", "Status", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {all.map((inv) => (
              <tr key={inv.id} className="border-t border-slate-800 hover:bg-slate-900/60" data-testid={`invoice-row-${inv.id}`}>
                <td className="px-5 py-4"><p className="num font-semibold">{inv.number}</p><p className="text-xs text-slate-500">{inv.created_at?.slice(0, 10)}{inv.due_date ? ` · due ${inv.due_date}` : ""}</p></td>
                <td data-label="Client" className="px-5 py-4"><p className="text-slate-200">{inv.client?.name || "—"}</p><p className="text-xs text-slate-500">{inv.client?.company}</p></td>
                <td data-label="Tier / study" className="px-5 py-4"><p className="text-slate-300">{inv.tier_label}</p><p className="text-xs text-slate-500 truncate max-w-[220px]">{inv.study_name || inv.description}</p></td>
                <td data-label="Amount" className="px-5 py-4 num font-bold">{money(inv.amount, inv.currency)}</td>
                <td data-label="Status" className="px-5 py-4"><span data-testid={`invoice-status-${inv.id}`} className={`text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${STATUS[inv.status]}`}>{inv.status}</span>
                  {inv.payment_method && <p className="text-[10px] text-slate-500 mt-1">via {inv.payment_method}</p>}
                  {inv.emailed_at && inv.status === "unpaid" && <p className="text-[10px] text-emerald-400 mt-1">emailed {inv.emailed_at.slice(0, 10)}</p>}
                  {inv.reminders_paused && inv.status === "unpaid" && <p className="text-[10px] text-amber-400 mt-1" data-testid={`invoice-paused-${inv.id}`}>reminders paused</p>}
                  {inv.reminder_count > 0 && inv.status === "unpaid" && <p className="text-[10px] text-amber-400 mt-1" data-testid={`invoice-reminders-${inv.id}`}>{inv.reminder_count} reminder{inv.reminder_count > 1 ? "s" : ""} sent</p>}</td>
                <td className="px-5 py-4 text-right whitespace-nowrap max-md:flex max-md:flex-wrap max-md:gap-1">
                  {inv.status === "unpaid" && <Button size="icon" variant="ghost" title={inv.client?.email ? "Email invoice to client" : "Client has no email"} disabled={!inv.client?.email} data-testid={`email-invoice-${inv.id}`} className="hover:text-emerald-400" onClick={() => emailInv(inv)}><Send className="h-4 w-4" /></Button>}
                  {inv.status === "unpaid" && <Button size="icon" variant="ghost" title={inv.reminders_paused ? "Resume reminders" : "Pause reminders"} data-testid={`pause-reminders-${inv.id}`} className={inv.reminders_paused ? "text-amber-400" : ""} onClick={() => togglePause(inv)}>{inv.reminders_paused ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}</Button>}
                  <Button size="icon" variant="ghost" title="Copy payment link" data-testid={`copy-link-${inv.id}`} onClick={() => copy(inv)}><Copy className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" asChild title="Open payment page"><a href={`/pay/${inv.token}`} target="_blank" rel="noreferrer" data-testid={`open-pay-${inv.id}`}><ExternalLink className="h-4 w-4" /></a></Button>
                  {inv.status === "unpaid" && <Button size="icon" variant="ghost" title="Mark paid" data-testid={`mark-paid-${inv.id}`} className="hover:text-emerald-400" onClick={() => setStatus(inv, "paid")}><CheckCircle2 className="h-4 w-4" /></Button>}
                  {inv.status === "unpaid" && <Button size="icon" variant="ghost" title="Void" data-testid={`void-${inv.id}`} onClick={() => setStatus(inv, "void")}><Ban className="h-4 w-4" /></Button>}
                  {!user?.impersonator && (inv.status !== "paid" || user?.role === "admin") && <DeleteInvoiceButton inv={inv} onDeleted={load} />}
                </td>
              </tr>
            ))}
            {invoices && all.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500" data-testid="invoices-empty">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <InvoiceDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)} initial={dialog} clients={clients} studies={studies} settings={settings} onSubmit={create} />
    </div>
  );
}
